import { NavLink } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  Calendar,
  UserCheck,
  BarChart3,
  Building2,
  Settings,
  Sparkles
} from 'lucide-react'
import { useAuth } from '../../hooks/useAuthHook'
import { cn } from '../../lib/utils'

type IconType = React.ComponentType<{ className?: string }>
type NavItem = { name: string; href: string; icon: IconType; role?: string; permission?: string }

export const navigation: NavItem[] = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Customers', href: '/customers', icon: Users, permission: 'view_customers' },
  { name: 'Bookings', href: '/bookings', icon: Calendar, permission: 'view_bookings' },
  { name: 'Rooms', href: '/rooms', icon: Building2, permission: 'view_bookings' },
  { name: 'Staff', href: '/staff', icon: UserCheck, permission: 'view_staff' },
  { name: 'Reports', href: '/reports', icon: BarChart3, permission: 'view_reports' },
]

export const adminNavigation: NavItem[] = [
  { name: 'Studios', href: '/studios', icon: Building2, role: 'Admin' },
  { name: 'Settings', href: '/settings', icon: Settings },
  // Global admin
  { name: 'Global Admin', href: '/admin', icon: Sparkles, role: 'SuperAdmin' },
]

export function Sidebar() {
  const { user } = useAuth()

  const canAccess = (item: NavItem) => {
    if (item.role && user?.role !== item.role) return false
    if (item.permission && !user?.permissions.includes(item.permission)) return false
    return true
  }

  return (
    <aside className="hidden md:flex md:flex-col w-72 bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl border-r border-gray-200 dark:border-gray-800 shadow-soft min-h-[calc(100vh-73px)]">
      {/* Sidebar header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center shadow-soft">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Navigation</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Studio Manager</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 custom-scrollbar overflow-y-auto">
        {/* Main navigation */}
        <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-3">
          Main
        </div>
        {navigation.map((item) => {
          if (!canAccess(item)) return null

          const Icon = item.icon
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 group',
                  isActive
                    ? 'bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-500/10 dark:to-primary-600/10 text-primary-700 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-primary-50/50 dark:hover:bg-primary-500/5 hover:text-primary-700 dark:hover:text-primary-400'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={cn(
                    "h-5 w-5 transition-transform group-hover:scale-110",
                    isActive ? "text-primary-600 dark:text-primary-400" : ""
                  )} />
                  {item.name}
                  {isActive && (
                    <div className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-500 animate-pulse" />
                  )}
                </>
              )}
            </NavLink>
          )
        })}

        {/* Admin navigation */}
        {adminNavigation.some(item => canAccess(item)) && (
          <>
            <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider px-3 mb-3 mt-6">
              Admin
            </div>
            {adminNavigation.map((item) => {
              if (!canAccess(item)) return null

              const Icon = item.icon
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 group',
                      isActive
                        ? 'bg-gradient-to-r from-accent-50 to-accent-100 dark:from-accent-500/10 dark:to-accent-600/10 text-accent-700 dark:text-accent-400 shadow-sm'
                        : 'text-gray-600 dark:text-gray-300 hover:bg-accent-50/50 dark:hover:bg-accent-500/5 hover:text-accent-700 dark:hover:text-accent-400'
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon className={cn(
                        "h-5 w-5 transition-transform group-hover:scale-110",
                        isActive ? "text-accent-600 dark:text-accent-400" : ""
                      )} />
                      {item.name}
                      {isActive && (
                        <div className="ml-auto w-1.5 h-1.5 rounded-full bg-accent-500 animate-pulse" />
                      )}
                    </>
                  )}
                </NavLink>
              )
            })}
          </>
        )}
      </nav>

      {/* Sidebar footer */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-800">
        <div className="p-4 rounded-xl bg-gradient-to-br from-primary-50 to-accent-50 dark:from-primary-500/10 dark:to-accent-500/10 border border-primary-200 dark:border-primary-800/30">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-white dark:bg-gray-800/50">
              <Sparkles className="h-4 w-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="flex-1">
              <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Need Help?
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
                Check out our documentation and guides
              </p>
              <a
                href="#"
                className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
              >
                View Docs →
              </a>
            </div>
          </div>
        </div>
      </div>
    </aside>
  )
}
